import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Wrench, Plus, X, Calendar, AlertTriangle, Search, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { toast } from 'sonner';
import CustomerTabsBar from '../../components/CustomerTabsBar';
import { useAuth } from '../../contexts/AuthContext';
import { EquipmentApprovalPanel } from '../../components/equipment/EquipmentApprovalPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ASSET_TYPES = [
  { key: 'water_heater',       label: 'Water Heater'        },
  { key: 'tankless_heater',    label: 'Tankless Heater'     },
  { key: 'water_softener',     label: 'Water Softener'      },
  { key: 'filtration_system',  label: 'Filtration System'   },
  { key: 'sump_pump',          label: 'Sump Pump'           },
  { key: 'ejector_pump',       label: 'Ejector Pump'        },
  { key: 'sewage_ejector',     label: 'Sewage Ejector'      },
  { key: 'backflow_preventer', label: 'Backflow Preventer'  },
  { key: 'prv',                label: 'PRV (Pressure Reducing Valve)' },
  { key: 'expansion_tank',     label: 'Expansion Tank'      },
  { key: 'recirc_pump',        label: 'Recirculation Pump'  },
  { key: 'garbage_disposal',   label: 'Garbage Disposal'    },
  { key: 'main_shutoff',       label: 'Main Shutoff Valve'  },
  { key: 'hose_bib',           label: 'Hose Bib'            },
  { key: 'sewer_line',         label: 'Sewer Line'          },
  { key: 'well_pump',          label: 'Well Pump'           },
  { key: 'backup_sump_pump',   label: 'Backup Sump Pump'    },
  { key: 'toilet',             label: 'Toilet'              },
  { key: 'faucet',             label: 'Faucet'              },
  { key: 'shower_valve',       label: 'Shower Valve'        },
  { key: 'tub_shower_fixture', label: 'Tub/Shower Fixture'  },
  { key: 'kitchen_sink',       label: 'Kitchen Sink'        },
  { key: 'bathroom_sink',      label: 'Bathroom Sink'       },
  { key: 'laundry_box',        label: 'Laundry Box'         },
  { key: 'ice_maker_box',      label: 'Ice Maker Box'       },
  { key: 'dishwasher_connection', label: 'Dishwasher Connection' },
  { key: 'gas_line',           label: 'Gas Line'            },
  { key: 'floor_drain',        label: 'Floor Drain'         },
  { key: 'cleanout',           label: 'Cleanout'            },
  { key: 'water_service',      label: 'Water Service'       },
  { key: 'well_system',        label: 'Well System'         },
  { key: 'pressure_tank',      label: 'Pressure Tank'       },
  { key: 'other',              label: 'Other'               },
];

// iter123 — Wave A-1 — Plumbing Areas / Systems. Broad inspection areas that
// aren't a single serial-numbered piece of equipment. No brand/model/warranty
// required — just condition notes + recommendations + location.
const AREA_TYPES = [
  { key: 'kitchen_sink',         label: 'Kitchen Sink' },
  { key: 'master_bath',          label: 'Master Bathroom' },
  { key: 'hall_bath',            label: 'Hall Bathroom' },
  { key: 'powder_room',          label: 'Powder Room' },
  { key: 'laundry_room',         label: 'Laundry Room' },
  { key: 'basement_mechanical',  label: 'Basement / Mechanical Room' },
  { key: 'sump_area',            label: 'Sump Area' },
  { key: 'main_water_service',   label: 'Main Water Service' },
  { key: 'drainage_system',      label: 'Drainage System' },
  { key: 'exterior_hose_bibs',   label: 'Exterior Hose Bibs' },
  { key: 'other_area',           label: 'Other Area' },
];

const RECORD_KINDS = [
  {
    key: 'equipment',
    label: 'Equipment Asset',
    blurb: 'Water heater, sump pump, softener — a single piece of equipment with brand / model / serial.',
  },
  {
    key: 'area',
    label: 'Plumbing Area / System',
    blurb: 'Kitchen sink, bathroom, sump area, main water service — broader area with condition notes and recommendations.',
  },
  {
    key: 'inspection',
    label: 'Inspection Report',
    blurb: 'Summary + recommendations from a whole-home visit. Can link to other records on this customer.',
  },
];

const STATUSES = [
  { key: 'active',        label: 'Active',        cls: 'bg-green-900/30 text-green-400 border-green-700/40' },
  { key: 'needs_service', label: 'Needs Service', cls: 'bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40' },
  { key: 'retired',       label: 'Retired',       cls: 'bg-[#262626] text-[#A3A3A3] border-[#3f3f3f]' },
];

const typeLabel = (t) => ASSET_TYPES.find((x) => x.key === t)?.label || t;
const areaLabel = (a) => AREA_TYPES.find((x) => x.key === a)?.label || a;
const statusMeta = (s) => STATUSES.find((x) => x.key === s) || STATUSES[0];

// iter123 — Strip placeholder strings on display so old prod data
// ("unknown", "n/a", "tbd", etc.) doesn't leak into UI cards. Backend already
// sanitizes on read/write (iter118); this is a belt-and-suspenders fallback for
// older docs that bypassed it.
const PLACEHOLDER_RE = /^(unknown|n\/?a|none|n\.a\.|tbd|null|-|—|\?)$/i;
const clean = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v).trim();
  return PLACEHOLDER_RE.test(s) ? '' : s;
};

const cardTitle = (a) => {
  if (a.record_kind === 'area') return areaLabel(a.area_type);
  if (a.record_kind === 'inspection') return clean(a.title) || 'Inspection Report';
  return typeLabel(a.type);
};
const cardSubtitle = (a) => {
  const brand = clean(a.brand);
  const model = clean(a.model);
  if (brand && model) return `${brand} — ${model}`;
  if (brand) return brand;
  if (model) return model;
  return '';
};

const today = () => new Date().toISOString().slice(0, 10);
const fmt = (s) => { if (!s) return '—'; try { return new Date(s + 'T00:00:00').toLocaleDateString(); } catch { return s; } };
const daysFromToday = (dateStr) => {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return Math.round((d - new Date()) / (1000 * 60 * 60 * 24));
  } catch { return null; }
};

const emptyForm = {
  customer_id: '',
  // iter123 — Plumbing Profile: equipment | area | inspection
  record_kind: 'equipment',
  type: 'water_heater',
  area_type: 'kitchen_sink',
  title: '',
  inspection_date: '',
  summary: '',
  recommendations: '',
  linked_record_ids: [],
  condition_notes: '',
  brand: '',
  model: '',
  serial: '',
  location: '',
  install_date: '',
  warranty_until: '',
  next_service_date: '',
  last_service_date: '',
  notes: '',
  status: 'active',
};

const AssetTracking = () => {
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [counts, setCounts] = useState({ by_type: {}, by_status: {}, due_soon: 0, overdue: 0, total: 0 });
  const [filters, setFilters] = useState({ type: '', status: '', customer_id: '', q: '', due_soon: false, scope: '' });
  const [searchParams, setSearchParams] = useSearchParams();
  const approvalView = searchParams.get('approval') || '';

  // Initialize scope from URL param so /admin/assets?scope=pm_property opens PM-filtered.
  // iter117 — Also accept ?customer_id=… so deep-links from customer profile pre-select.
  useEffect(() => {
    const s = searchParams.get('scope');
    const cid = searchParams.get('customer_id');
    if (s || cid) {
      setFilters((f) => ({
        ...f,
        ...(s ? { scope: s } : {}),
        ...(cid ? { customer_id: cid } : {}),
      }));
    }
  }, [searchParams]);
  const [drawer, setDrawer] = useState(null);   // null | 'new' | 'saved' | asset object
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  // iter117 — Searchable customer picker + "Add Another Asset" loop state.
  const [customerQuery, setCustomerQuery] = useState('');
  const [justSavedAssetName, setJustSavedAssetName] = useState('');
  const autoOpenedRef = useRef(false);

  const loadAssets = useCallback(async () => {
    const params = {};
    if (filters.type) params.type = filters.type;
    if (filters.status) params.status = filters.status;
    if (filters.customer_id) params.customer_id = filters.customer_id;
    if (filters.scope) params.scope = filters.scope;
    if (filters.due_soon) params.due_soon_days = 30;
    const { data } = await axios.get(`${API}/admin/assets`, { withCredentials: true, params });
    setAssets(data);
  }, [filters]);

  const loadCustomers = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/customers`, { withCredentials: true });
    setCustomers(data);
  }, []);

  const loadCounts = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/assets/counts`, { withCredentials: true });
    setCounts(data);
  }, []);

  useEffect(() => { loadCustomers(); loadCounts(); }, [loadCustomers, loadCounts]);
  useEffect(() => { loadAssets(); }, [loadAssets]);

  // iter117 — Optionally pre-select a customer. Also reset the customer-picker
  // search box.
  const openCreate = useCallback((preselectCustomerId) => {
    const cid = preselectCustomerId || filters.customer_id || '';
    setForm({ ...emptyForm, customer_id: cid });
    setCustomerQuery('');
    setDrawer('new');
  }, [filters.customer_id]);

  // iter117 — Auto-open the Add Asset drawer with customer pre-selected when the
  // page is opened as /admin/assets?customer_id=…&add=1 (e.g. an "Add Equipment"
  // button on a customer profile). Runs once after customers load so the
  // selectedCustomer chip can render the name immediately.
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (customers.length === 0) return;
    const cid = searchParams.get('customer_id');
    if (cid && searchParams.get('add') === '1') {
      autoOpenedRef.current = true;
      openCreate(cid);
    }
  }, [customers, searchParams, openCreate]);

  // iter117 — "Add Another Asset" — reset everything EXCEPT the customer so the
  // tech / admin can rapidly enter a water heater → sump pump → softener etc.
  // for the same customer without re-searching.
  const addAnother = () => {
    const keepCustomer = form.customer_id;
    setForm({ ...emptyForm, customer_id: keepCustomer });
    setJustSavedAssetName('');
    setDrawer('new');
  };

  const openEdit = (a) => {
    setForm({
      ...emptyForm,
      ...a,
      install_date: a.install_date || '',
      warranty_until: a.warranty_until || '',
      next_service_date: a.next_service_date || '',
      last_service_date: a.last_service_date || '',
    });
    setDrawer(a);
  };

  const save = async () => {
    if (!form.customer_id) { toast.error('Choose a customer first'); return; }
    // iter123 — Validate by record_kind. Equipment needs a type, Area needs an
    // area_type, Inspection needs at least a title OR an inspection date so the
    // card has something meaningful to show.
    const kind = form.record_kind || 'equipment';
    if (kind === 'equipment' && !form.type) { toast.error('Equipment type required'); return; }
    if (kind === 'area' && !form.area_type) { toast.error('Area type required'); return; }
    if (kind === 'inspection' && !form.title?.trim() && !form.inspection_date) {
      toast.error('Give the inspection a title or a date');
      return;
    }
    setBusy(true);
    try {
      if (drawer === 'new') {
        const { data } = await axios.post(`${API}/admin/assets`, form, { withCredentials: true });
        if (data?.warning === 'duplicate_serial') {
          const dup = data.duplicate || {};
          const ok = window.confirm(
            `Possible duplicate asset found.\n\nExisting: ${dup.brand || ''} ${dup.model || ''} (${dup.type}) — Serial ${dup.serial} on ${dup.customer_name || dup.scope}.\n\nProceed and flag this for manager review?`
          );
          if (!ok) { setBusy(false); return; }
          await axios.post(`${API}/admin/assets`, { ...form, confirm_duplicate: true }, { withCredentials: true });
          toast.warning('Asset created and flagged for manager review (duplicate serial)');
        } else {
          toast.success('Asset added');
        }
        // iter117 — Flip drawer to a "Saved" confirmation that exposes Done +
        // Add Another Asset, instead of closing. The customer stays attached so
        // the next asset reuses it.
        const kindLabel = (form.record_kind === 'area')
          ? areaLabel(form.area_type)
          : (form.record_kind === 'inspection')
            ? (form.title?.trim() || 'Inspection Report')
            : `${typeLabel(form.type)}${form.brand ? ' — ' + form.brand : ''}`;
        setJustSavedAssetName(kindLabel);
        setDrawer('saved');
        loadAssets();
        loadCounts();
        setBusy(false);
        return;
      } else {
        const { id, customer_id, customer_name, customer_address, created_at, created_by_id, created_by_name, updated_at, updated_by_id, ...patch } = form;
        const { data } = await axios.put(`${API}/admin/assets/${drawer.id}`, patch, { withCredentials: true });
        if (data?.warning === 'duplicate_serial') {
          const ok = window.confirm('Possible duplicate serial detected. Flag for manager review and save anyway?');
          if (!ok) { setBusy(false); return; }
          await axios.put(`${API}/admin/assets/${drawer.id}`, { ...patch, confirm_duplicate: true }, { withCredentials: true });
          toast.warning('Asset updated and flagged for manager review');
        } else {
          toast.success('Asset updated');
        }
      }
      setDrawer(null);
      loadAssets();
      loadCounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setBusy(false);
  };

  const remove = async () => {
    if (!drawer || drawer === 'new') return;
    if (!window.confirm(`Delete this ${cardTitle(drawer)}? This can't be undone.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/assets/${drawer.id}`, { withCredentials: true });
      toast.success('Asset deleted');
      setDrawer(null);
      loadAssets();
      loadCounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
    setBusy(false);
  };

  const filteredBySearch = filters.q.trim()
    ? assets.filter((a) => `${a.brand} ${a.model} ${a.serial} ${a.customer_name} ${a.location}`.toLowerCase().includes(filters.q.toLowerCase()))
    : assets;

  // iter117 — Searchable customer picker for the Add Asset drawer. Matches by
  // name, phone, email, OR service address so techs can type whatever's on hand.
  const filteredCustomersForPicker = useMemo(() => {
    const q = customerQuery.trim().toLowerCase();
    const list = !q ? customers : customers.filter((c) =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.phone || '').toLowerCase().includes(q) ||
      (c.address || '').toLowerCase().includes(q) ||
      (c.billing_address || '').toLowerCase().includes(q)
    );
    return list.slice(0, 50);
  }, [customers, customerQuery]);
  const selectedCustomer = customers.find((c) => c.id === form.customer_id);

  return (
    <div data-testid="asset-tracking-page" className="space-y-6">
      <CustomerTabsBar userRole={user?.role} />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">Plumbing Profile</h2>
          <p className="text-[#A3A3A3]">Equipment, plumbing areas, and inspection reports for every customer. Built for full-home plumbing, not just water heaters.</p>
        </div>
        <Button data-testid="asset-add-btn" onClick={() => openCreate()} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
          <Plus className="w-4 h-4 mr-2" /> Add Record
        </Button>
      </div>

      {/* iter328 — Approvals live on this canonical page (Command Center deep-links ?approval=pending). */}
      <div className="flex items-center gap-2" data-testid="asset-view-toggle">
        <button data-testid="asset-view-records" onClick={() => setSearchParams((p) => { p.delete('approval'); return p; })}
          className={`px-3 py-1.5 rounded-md text-xs border ${!approvalView ? 'bg-white text-black border-white font-semibold' : 'border-[#262626] text-[#A3A3A3] hover:text-white'}`}>All Records</button>
        <button data-testid="asset-view-approvals" onClick={() => setSearchParams((p) => { p.set('approval', 'pending'); return p; })}
          className={`px-3 py-1.5 rounded-md text-xs border ${approvalView ? 'bg-white text-black border-white font-semibold' : 'border-[#262626] text-[#A3A3A3] hover:text-white'}`}>Equipment Approvals</button>
      </div>
      {approvalView && <EquipmentApprovalPanel state={approvalView} setState={(s) => setSearchParams((p) => { p.set('approval', s); return p; })} onChanged={() => { loadAssets(); loadCounts(); }} />}

      {!approvalView && <>
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile testid="asset-tile-total"     label="Total Assets"   value={counts.total} accent="text-white" />
        <Tile testid="asset-tile-active"    label="Active"         value={counts.by_status?.active ?? 0} accent="text-green-400" />
        <Tile testid="asset-tile-needs"     label="Needs Service"  value={counts.by_status?.needs_service ?? 0} accent="text-[#D4AF37]" />
        <Tile testid="asset-tile-due-soon"  label="Due in 30 Days" value={counts.due_soon} accent="text-blue-400" onClick={() => setFilters({ ...filters, due_soon: !filters.due_soon })} active={filters.due_soon} />
        <Tile testid="asset-tile-overdue"   label="Overdue"        value={counts.overdue} accent="text-red-400" />
      </div>

      {/* Scope tabs (Wave A #12) */}
      <div data-testid="asset-scope-tabs" className="flex items-center gap-2 flex-wrap">
        {[
          { key: '',           label: 'All'           },
          { key: 'residential',label: 'Residential'   },
          { key: 'commercial', label: 'Commercial'    },
          { key: 'pm_property',label: 'Property Mgmt' },
        ].map((s) => (
          <button
            key={s.key || 'all'}
            data-testid={`asset-scope-${s.key || 'all'}`}
            onClick={() => setFilters({ ...filters, scope: s.key })}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
              filters.scope === s.key
                ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0A0A0A]'
                : 'bg-[#0A0A0A] border-[#262626] text-[#A3A3A3] hover:border-[#3f3f3f] hover:text-white'
            }`}
          >
            {s.label} <span className="opacity-70">({counts.by_scope?.[s.key] ?? (s.key === '' ? counts.total : 0)})</span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="asset-filters">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-[#A3A3A3] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="asset-search"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder="Search by brand, model, serial, customer, location"
            className="pl-10 bg-[#0A0A0A] border-[#262626] text-white"
          />
        </div>
        <Select value={filters.type || 'all'} onValueChange={(v) => setFilters({ ...filters, type: v === 'all' ? '' : v })}>
          <SelectTrigger data-testid="asset-filter-type" className="w-[180px] bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-[#262626]">
            <SelectItem value="all" className="text-white">All Types</SelectItem>
            {ASSET_TYPES.map((t) => <SelectItem key={t.key} value={t.key} className="text-white">{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.status || 'all'} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
          <SelectTrigger data-testid="asset-filter-status" className="w-[160px] bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-[#262626]">
            <SelectItem value="all" className="text-white">All Statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s.key} value={s.key} className="text-white">{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.customer_id || 'all'} onValueChange={(v) => setFilters({ ...filters, customer_id: v === 'all' ? '' : v })}>
          <SelectTrigger data-testid="asset-filter-customer" className="w-[220px] bg-[#0A0A0A] border-[#262626] text-white"><SelectValue placeholder="Customer" /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-[#262626] max-h-[300px]">
            <SelectItem value="all" className="text-white">All Customers</SelectItem>
            {customers.map((c) => <SelectItem key={c.id} value={c.id} className="text-white">{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      <div className="grid gap-3">
        {filteredBySearch.length === 0 ? (
          <div className="bg-[#141414] border border-[#262626] rounded-md p-10 text-center">
            <Wrench className="w-8 h-8 text-[#525252] mx-auto mb-3" />
            <p className="text-white font-medium">No records match these filters.</p>
            <p className="text-[#A3A3A3] text-sm mt-1">Click <span className="text-[#D4AF37]">Add Record</span> to start tracking equipment, areas, or inspections.</p>
          </div>
        ) : filteredBySearch.map((a) => {
          const sMeta = statusMeta(a.status);
          const daysToService = daysFromToday(a.next_service_date);
          const overdue = daysToService !== null && daysToService < 0 && a.status !== 'retired';
          const warrantyDays = daysFromToday(a.warranty_until);
          return (
            <button
              key={a.id}
              data-testid={`asset-row-${a.id}`}
              onClick={() => openEdit(a)}
              className="bg-[#141414] border border-[#262626] rounded-md p-4 text-left hover:border-[#D4AF37] transition-colors w-full"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Wrench className="w-4 h-4 text-[#D4AF37]" />
                    <p className="text-white font-medium">{cardTitle(a)}</p>
                    {a.record_kind && a.record_kind !== 'equipment' && (
                      <span className="text-[10px] tracking-wider uppercase px-1.5 py-0.5 rounded border font-bold bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/40">
                        {a.record_kind === 'area' ? 'Area' : 'Inspection'}
                      </span>
                    )}
                    <span className={`text-[10px] tracking-wider uppercase px-2 py-0.5 rounded border font-bold ${sMeta.cls}`}>{sMeta.label}</span>
                    {overdue && (
                      <span className="text-[10px] tracking-wider uppercase px-2 py-0.5 rounded border font-bold bg-red-900/30 text-red-400 border-red-700/40 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> {Math.abs(daysToService)}d overdue
                      </span>
                    )}
                  </div>
                  {/* iter123 — Subtitle only renders when there's real brand/model data.
                      No more "unknown — unknown" or lone "—". */}
                  {cardSubtitle(a) && (
                    <p className="text-[#D4AF37] text-sm font-medium">{cardSubtitle(a)}</p>
                  )}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[#A3A3A3] text-xs mt-2">
                    <span>Customer: <span className="text-white">{a.customer_name || '—'}</span></span>
                    {clean(a.serial) && <span>Serial: <span className="text-white">{clean(a.serial)}</span></span>}
                    {clean(a.location) && <span>Loc: <span className="text-white">{clean(a.location)}</span></span>}
                  </div>
                </div>
                <div className="text-right shrink-0 min-w-[160px]">
                  <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider">Next Service</p>
                  <p className={`text-sm font-medium ${overdue ? 'text-red-400' : daysToService !== null && daysToService <= 30 ? 'text-[#D4AF37]' : 'text-white'}`}>
                    {fmt(a.next_service_date)}
                  </p>
                  {warrantyDays !== null && (
                    <p className="text-[10px] text-[#A3A3A3] mt-1">
                      Warranty: <span className={warrantyDays < 0 ? 'text-red-400' : 'text-white'}>{fmt(a.warranty_until)}</span>
                    </p>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      </>}

      {/* Drawer */}
      <Sheet open={!!drawer} onOpenChange={(o) => { if (!o) { setDrawer(null); setJustSavedAssetName(''); } }}>
        <SheetContent className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-lg overflow-y-auto" data-testid="asset-drawer">
          <SheetHeader>
            <SheetTitle className="text-white">
              {drawer === 'new'   ? 'Add Record'
               : drawer === 'saved' ? 'Record Saved'
               : `Edit ${cardTitle(form)}`}
            </SheetTitle>
          </SheetHeader>

          {/* iter117 — Saved-confirmation state. Lets the admin/tech rapidly add
              multiple assets for the same customer without re-searching. */}
          {drawer === 'saved' ? (
            <div className="space-y-5 mt-6" data-testid="asset-saved-confirmation">
              <div className="bg-[#0A0A0A] border border-green-700/40 rounded-md p-4 flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-green-400 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-white font-medium">{justSavedAssetName || 'Record saved'}</p>
                  <p className="text-[#A3A3A3] text-xs mt-0.5">
                    Saved to <span className="text-white">{selectedCustomer?.name || 'customer'}</span>. Add another record or close.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  data-testid="asset-add-another-btn"
                  onClick={addAnother}
                  className="flex-1 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Another Record
                </Button>
                <Button
                  data-testid="asset-done-btn"
                  onClick={() => { setDrawer(null); setJustSavedAssetName(''); }}
                  variant="outline"
                  className="flex-1 border-[#262626] text-white hover:bg-[#262626]"
                >
                  Done
                </Button>
              </div>
              <p className="text-[10px] text-[#525252] text-center">
                Tip: customer stays attached. Just pick the next equipment type and save.
              </p>
            </div>
          ) : (
          <div className="space-y-4 mt-6">
            {/* iter117 — Searchable Customer picker. Replaces the old <Select>
                so a 500-customer list is usable. Searches by name / phone /
                email / service address. */}
            <div className="space-y-1.5">
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Customer</Label>
              {selectedCustomer ? (
                <div data-testid="asset-form-selected-customer" className="bg-[#0A0A0A] border border-[#D4AF37]/50 rounded-md p-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-white font-medium truncate">{selectedCustomer.name}</p>
                    <p className="text-[#A3A3A3] text-xs truncate">
                      {selectedCustomer.phone || 'no phone'} · {selectedCustomer.email || 'no email'}
                    </p>
                    {selectedCustomer.address && (
                      <p className="text-[#A3A3A3] text-xs truncate">{selectedCustomer.address}</p>
                    )}
                  </div>
                  {/* iter117 — Customer is immutable on existing assets. Show the
                      'Change' affordance only when creating a brand-new asset. */}
                  {drawer === 'new' && (
                    <Button
                      data-testid="asset-form-change-customer"
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-[#262626] text-white hover:bg-[#262626] h-8 shrink-0"
                      onClick={() => setForm({ ...form, customer_id: '' })}
                    >
                      Change
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#525252]" />
                    <Input
                      data-testid="asset-form-customer-search"
                      autoFocus
                      value={customerQuery}
                      onChange={(e) => setCustomerQuery(e.target.value)}
                      placeholder="Search name, phone, email, or address…"
                      className="pl-8 bg-[#0A0A0A] border-[#262626] text-white h-11 text-base"
                    />
                  </div>
                  <div className="bg-[#0A0A0A] border border-[#262626] rounded-md max-h-56 overflow-y-auto divide-y divide-[#1f1f1f]">
                    {filteredCustomersForPicker.length === 0 ? (
                      <div data-testid="asset-form-customer-no-match" className="w-full p-3 text-[#A3A3A3] text-sm">
                        No customers match. Try a different search term.
                      </div>
                    ) : (
                      filteredCustomersForPicker.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          data-testid={`asset-form-customer-option-${c.id}`}
                          onClick={() => { setForm({ ...form, customer_id: c.id }); setCustomerQuery(''); }}
                          className="w-full text-left p-3 hover:bg-[#1a1a1a] text-sm"
                        >
                          <p className="text-white font-medium">{c.name}</p>
                          <p className="text-[#A3A3A3] text-xs">{c.phone || 'no phone'} · {c.email || 'no email'}</p>
                          {c.address && <p className="text-[#A3A3A3] text-[11px] truncate">{c.address}</p>}
                        </button>
                      ))
                    )}
                  </div>
                  {customers.length > 50 && !customerQuery && (
                    <p className="text-[10px] text-[#525252]">Showing first 50 — type to narrow down.</p>
                  )}
                </>
              )}
            </div>

            {/* iter123 Wave A-1 — Record-kind picker. Editable on create, locked on edit. */}
            <div className="space-y-2" data-testid="asset-form-kind-picker">
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Record Type</Label>
              {drawer === 'new' ? (
                <div className="grid grid-cols-1 gap-2">
                  {RECORD_KINDS.map((k) => (
                    <button
                      key={k.key}
                      type="button"
                      data-testid={`asset-form-kind-${k.key}`}
                      onClick={() => setForm({ ...form, record_kind: k.key })}
                      className={`text-left rounded-md p-3 border transition-colors ${
                        form.record_kind === k.key
                          ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                          : 'border-[#262626] bg-[#0A0A0A] hover:border-[#3f3f3f]'
                      }`}
                    >
                      <p className={`text-sm font-bold ${form.record_kind === k.key ? 'text-[#D4AF37]' : 'text-white'}`}>{k.label}</p>
                      <p className="text-[11px] text-[#A3A3A3] mt-0.5">{k.blurb}</p>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="rounded-md p-2.5 border border-[#262626] bg-[#0A0A0A]">
                  <p className="text-[#D4AF37] text-sm font-bold">{RECORD_KINDS.find((k) => k.key === (form.record_kind || 'equipment'))?.label || 'Equipment Asset'}</p>
                  <p className="text-[10px] text-[#525252]">Record type can&apos;t be changed after creation.</p>
                </div>
              )}
            </div>

            {/* ─── EQUIPMENT ASSET form (existing) ─── */}
            {form.record_kind === 'equipment' && (
            <div data-testid="asset-form-equipment" className="grid grid-cols-2 gap-3">
              <Field label="Type">
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="asset-form-type" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    {ASSET_TYPES.map((t) => <SelectItem key={t.key} value={t.key} className="text-white">{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="asset-form-status" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    {STATUSES.map((s) => <SelectItem key={s.key} value={s.key} className="text-white">{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Brand">
                <Input data-testid="asset-form-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" placeholder="Bradford White" />
              </Field>
              <Field label="Model">
                <Input data-testid="asset-form-model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" placeholder="M-2-50T6FBN" />
              </Field>
              <Field label="Serial">
                <Input data-testid="asset-form-serial" value={form.serial} onChange={(e) => setForm({ ...form, serial: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </Field>
              <Field label="Location at Property" wide>
                <Input data-testid="asset-form-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" placeholder="Basement utility room" />
              </Field>
              <Field label="Install Date">
                <Input data-testid="asset-form-install" type="date" value={form.install_date} onChange={(e) => setForm({ ...form, install_date: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </Field>
              <Field label="Warranty Until">
                <Input data-testid="asset-form-warranty" type="date" value={form.warranty_until} onChange={(e) => setForm({ ...form, warranty_until: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </Field>
              <Field label="Last Service">
                <Input data-testid="asset-form-last" type="date" value={form.last_service_date} onChange={(e) => setForm({ ...form, last_service_date: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </Field>
              <Field label="Next Service Due">
                <Input data-testid="asset-form-next" type="date" value={form.next_service_date} onChange={(e) => setForm({ ...form, next_service_date: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </Field>
            </div>
            )}

            {/* ─── PLUMBING AREA form ─── */}
            {form.record_kind === 'area' && (
            <div data-testid="asset-form-area" className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Area Type">
                  <Select value={form.area_type} onValueChange={(v) => setForm({ ...form, area_type: v })}>
                    <SelectTrigger data-testid="asset-form-area-type" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">
                      {AREA_TYPES.map((t) => <SelectItem key={t.key} value={t.key} className="text-white">{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Status">
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">
                      {STATUSES.map((s) => <SelectItem key={s.key} value={s.key} className="text-white">{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Location" wide>
                <Input data-testid="asset-form-area-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. 2nd floor master suite" className="bg-[#0A0A0A] border-[#262626] text-white" />
              </Field>
              <Field label="Condition Notes" wide>
                <Textarea data-testid="asset-form-area-condition" value={form.condition_notes} onChange={(e) => setForm({ ...form, condition_notes: e.target.value })} rows={3} placeholder="Current condition, what you observed, anything dispatch should know…" className="bg-[#0A0A0A] border-[#262626] text-white" />
              </Field>
              <Field label="Recommendations" wide>
                <Textarea data-testid="asset-form-area-recommendations" value={form.recommendations} onChange={(e) => setForm({ ...form, recommendations: e.target.value })} rows={3} placeholder="Recommended follow-ups, repairs, upgrades…" className="bg-[#0A0A0A] border-[#262626] text-white" />
              </Field>
            </div>
            )}

            {/* ─── INSPECTION REPORT form (lightweight v1) ─── */}
            {form.record_kind === 'inspection' && (
            <div data-testid="asset-form-inspection" className="space-y-3">
              <Field label="Title" wide>
                <Input data-testid="asset-form-inspection-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Whole-home plumbing inspection — June 2026" className="bg-[#0A0A0A] border-[#262626] text-white" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Inspection Date">
                  <Input data-testid="asset-form-inspection-date" type="date" value={form.inspection_date} onChange={(e) => setForm({ ...form, inspection_date: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </Field>
                <Field label="Status">
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">
                      {STATUSES.map((s) => <SelectItem key={s.key} value={s.key} className="text-white">{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Summary" wide>
                <Textarea data-testid="asset-form-inspection-summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} rows={3} placeholder="What did you find overall?" className="bg-[#0A0A0A] border-[#262626] text-white" />
              </Field>
              <Field label="Recommendations" wide>
                <Textarea data-testid="asset-form-inspection-recommendations" value={form.recommendations} onChange={(e) => setForm({ ...form, recommendations: e.target.value })} rows={3} placeholder="Recommended follow-ups, repairs, equipment upgrades…" className="bg-[#0A0A0A] border-[#262626] text-white" />
              </Field>
              <p className="text-[10px] text-[#525252]">Tip: link this inspection to equipment / area records by opening this report after save (Wave A-2 will add per-photo upload + multi-link picker).</p>
            </div>
            )}
            <Field label="Notes" wide>
              <Textarea data-testid="asset-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="bg-[#0A0A0A] border-[#262626] text-white" />
            </Field>

            <div className="flex items-center gap-2 pt-4 border-t border-[#262626]">
              <Button data-testid="asset-form-save" onClick={save} disabled={busy} className="flex-1 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
                {busy ? 'Saving...' : drawer === 'new' ? 'Create Record' : 'Save Changes'}
              </Button>
              {drawer !== 'new' && (
                <Button data-testid="asset-form-delete" onClick={remove} disabled={busy} variant="outline" className="border-red-700/40 text-red-400 hover:bg-red-900/20">
                  Delete
                </Button>
              )}
            </div>
          </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

const Tile = ({ label, value, accent, testid, onClick, active }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    type="button"
    className={`bg-[#141414] border rounded-md p-4 text-left transition-colors ${onClick ? 'cursor-pointer hover:border-[#D4AF37]' : 'cursor-default'} ${active ? 'border-[#D4AF37]' : 'border-[#262626]'}`}
    disabled={!onClick}
  >
    <p className="text-[#A3A3A3] text-xs uppercase tracking-wider mb-1">{label}</p>
    <p className={`text-2xl font-bold ${accent || 'text-white'}`}>{value}</p>
  </button>
);

const Field = ({ label, wide, children }) => (
  <div className={`space-y-1.5 ${wide ? 'col-span-2' : ''}`}>
    <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">{label}</Label>
    {children}
  </div>
);

export default AssetTracking;
